import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { XMarkIcon, CogIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { consentApi, type CookieConsents } from '@/api/consent'

const STORAGE_KEY = 'edive-cookie-consent'
const POLICY_VERSION = 'privacy-2026-04-30'

function loadPrefs(): CookieConsents | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function savePrefs(prefs: CookieConsents) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
}

async function persistConsent(prefs: CookieConsents, userId: string) {
  try {
    await consentApi.saveCookie({
      userId,
      policyVersion: POLICY_VERSION,
      consents: prefs,
    })
  } catch { /* non-blocking */ }
}

export default function CookieBanner() {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  const [customizing, setCustomizing] = useState(false)
  const [prefs, setPrefs] = useState<CookieConsents>({
    necessary: true,
    functional: false,
    analytics: false,
    profiling: false,
    marketing: false,
  })
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userId = useAuthStore((s) => s.user?.id)

  useEffect(() => {
    const saved = loadPrefs()
    if (saved) {
      setPrefs(saved)
    } else if (isAuthenticated) {
      savePrefs({ necessary: true, functional: false, analytics: false, profiling: false, marketing: false })
      setShow(false)
    } else {
      setShow(true)
    }
  }, [isAuthenticated])

  if (!show) return null

  function accept(p: CookieConsents) {
    savePrefs(p)
    if (isAuthenticated && userId) persistConsent(p, userId)
    setShow(false)
  }

  function acceptAll() {
    accept({ necessary: true, functional: true, analytics: true, profiling: false, marketing: true })
  }

  function acceptNecessary() {
    accept({ necessary: true, functional: false, analytics: false, profiling: false, marketing: false })
  }

  function saveCustom() {
    accept(prefs)
  }

  const COOKIE_ITEMS: { key: keyof CookieConsents; label: string; desc: string; locked: boolean }[] = [
    {
      key: 'necessary',
      label: t('cookies.necessary'),
      desc: t('cookies.necessaryDesc'),
      locked: true,
    },
    {
      key: 'functional',
      label: t('cookies.functional'),
      desc: t('cookies.functionalDesc'),
      locked: false,
    },
    {
      key: 'analytics',
      label: t('cookies.analytics'),
      desc: t('cookies.analyticsDesc'),
      locked: false,
    },
    {
      key: 'marketing',
      label: t('cookies.marketing'),
      desc: t('cookies.marketingDesc'),
      locked: false,
    },
  ]

  return (
    <div className="fixed bottom-16 inset-x-0 z-40 px-3 pb-2 sm:px-4 sm:pb-3 lg:bottom-0 lg:px-6 lg:pb-6">
      <div className="max-w-4xl mx-auto bg-ocean-900 border border-ocean-700 rounded-2xl shadow-2xl text-white overflow-hidden">
        {!customizing ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-9 h-9 rounded-xl bg-ocean-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <ShieldCheckIcon className="w-5 h-5 text-ocean-400" />
              </div>
              <div>
                <p className="font-semibold text-sm mb-0.5">{t('cookies.title')}</p>
                <p className="text-white/60 text-xs leading-relaxed">
                  {t('cookies.desc')}{' '}
                  <Link to="/privacy-policy" className="text-ocean-400 hover:underline">
                    {t('auto.componentsCookieBanner.privacyPolicy')}
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              <button
                onClick={() => setCustomizing(true)}
                className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white px-3 py-2 rounded-lg border border-white/15 hover:border-white/30 transition-all"
              >
                <CogIcon className="w-3.5 h-3.5" />
                {t('cookies.customize')}
              </button>
              <button
                onClick={acceptNecessary}
                className="text-xs text-white/80 hover:text-white px-4 py-2 rounded-lg border border-white/20 hover:border-white/40 transition-all"
              >
                {t('cookies.acceptNecessary')}
              </button>
              <button
                onClick={acceptAll}
                className="text-xs bg-ocean-500 hover:bg-ocean-400 text-white font-semibold px-4 py-2 rounded-lg transition-all"
              >
                {t('cookies.acceptAll')}
              </button>
              <button onClick={acceptNecessary} className="text-white/40 hover:text-white p-2.5 rounded-lg transition-colors">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold">{t('cookies.preferences')}</p>
              <button onClick={() => setCustomizing(false)} className="text-white/40 hover:text-white">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 mb-5">
              {COOKIE_ITEMS.map(({ key, label, desc, locked }) => (
                <div key={key} className="flex items-start justify-between gap-4 bg-white/5 rounded-xl p-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-white/50 mt-0.5">{desc}</p>
                  </div>
                  <button
                    disabled={locked}
                    onClick={() => !locked && setPrefs((p) => ({ ...p, [key]: !p[key] }))}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 mt-0.5 ${
                      locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                    } ${prefs[key] ? 'bg-ocean-500' : 'bg-white/20'}`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        prefs[key] ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={acceptAll}
                className="flex-1 text-xs bg-ocean-500 hover:bg-ocean-400 text-white font-semibold py-2 rounded-lg transition-all"
              >
                {t('cookies.acceptAll')}
              </button>
              <button
                onClick={saveCustom}
                className="flex-1 text-xs border border-white/20 hover:border-white/40 text-white py-2 rounded-lg transition-all"
              >
                {t('cookies.savePreferences')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
